'use client';

import { useRef, MouseEvent as ReactMouseEvent, useId } from 'react';
import { SidebarProps } from './types';
import { Portal } from '../portal';
import clsx from 'classnames';
import Close from 'icons/lib/close_nav.svg';
import { useEffect } from 'react';
import { useScrollBlock } from '../../../hooks';

import styles from './Sidebar.module.scss';

let openedSidebarsWithBlockedScroll = 0;

const safeDocument: Document | null = typeof document !== 'undefined' ? document : null;
const body = safeDocument?.body;

export function Sidebar({
	children,
	id,
	isOpen,
	overlayClassName,
	side = 'right',
	wrapperClassName,
	onOverlayPress,
}: SidebarProps) {
	const sidebarId = `${id || 'sidebar'}-${useId()}`;
	const [blockScroll, allowScroll] = useScrollBlock();
	const customCursor = useRef<HTMLDivElement>(null);

	useEffect(() => {
		function handleEscapeKey(event: KeyboardEvent) {
			if (event.code !== 'Escape') {
				return;
			}

			onOverlayPress?.();
		}

		if (isOpen) {
			++openedSidebarsWithBlockedScroll;
			document.addEventListener('keydown', handleEscapeKey);
			blockScroll();
		} else {
			openedSidebarsWithBlockedScroll =
				openedSidebarsWithBlockedScroll === 0 ? 0 : openedSidebarsWithBlockedScroll - 1;
			openedSidebarsWithBlockedScroll === 0 && allowScroll();
			document.removeEventListener('keydown', handleEscapeKey);
		}
		return () => {
			document.removeEventListener('keydown', handleEscapeKey);
		};
	}, [isOpen]);

	const wrapperClasses = clsx(
		styles.sidebarWrapper,
		styles[`sidebarWrapper--${side}`],
		wrapperClassName,
		{
			[styles[`sidebarWrapper--open`]]: isOpen,
		}
	);
	const overlayClasses = clsx(styles.sidebarOverlay, overlayClassName, {
		[styles[`sidebarOverlay--open`]]: isOpen,
	});
	const closeButtonClasses = clsx(styles.sidebarDesktopCloseButton, {
		[styles[`sidebarDesktopCloseButton--open`]]: isOpen,
	});

	const customCursorHandle = (e: ReactMouseEvent<HTMLDivElement, MouseEvent>) => {
		if (!customCursor.current || !body || !body.style) {
			return;
		}

		const mouseY = e.clientY;
		const mouseX = e.clientX;

		customCursor.current.style.transform = `translate3d(${mouseX}px, ${mouseY}px, 0)`;
	};

	const customCursorOverflowLeave = () => {
		if (!customCursor.current) {
			return;
		}

		customCursor.current.style.transform = `translate3d(0, -40px, 0)`;
	};

	const onOverlayClick = () => {
		onOverlayPress?.();
	};

	return (
		<Portal>
			<aside id={sidebarId}>
				<div
					className={overlayClasses}
					onClick={onOverlayClick}
					onMouseMove={customCursorHandle}
					onMouseLeave={customCursorOverflowLeave}
				>
					{isOpen && (
						<div ref={customCursor} className={styles.sidebarOverlayCursor}>
							<Close />
						</div>
					)}
				</div>
				<div className={wrapperClasses}>
					<div className={styles.sidebarContent}>{children}</div>
				</div>
			</aside>
		</Portal>
	);
}
